import { Logo } from '../logo/logo';
import { useSignOut, useAuthUser } from 'react-auth-kit';
import { useNavigate, useLocation } from 'react-router-dom';
import './navbar.css';
import { useEffect, useRef, useState } from 'react';
import { useLanguage } from '../../i18n/LanguageProvider';
import { usePermissions } from '../../hooks/usePermissions';
import { usePlanStatus } from '../../hooks/usePlanStatus';
import { track } from '../../utils/track';
import { FlagGB, FlagSE } from '../icons';

interface NavBarProps {
  // Optional contextual control rendered inline in the sticky bar (e.g. the home
  // area selector, so it stays reachable when the page is scrolled).
  centerSlot?: React.ReactNode;
}

export const NavBar: React.FC<NavBarProps> = ({ centerSlot }) => {
  const signOut = useSignOut();
  const auth = useAuthUser();
  const navigate = useNavigate();
  const location = useLocation();
  const { language, setLanguage, t } = useLanguage();
  const { hasPermission } = usePermissions();
  const authData = auth();
  const userName = authData?.username || authData?.email?.split('@')[0] || 'User';
  const userInitial = (userName[0] || 'U').toUpperCase();

  const isAdmin = hasPermission('admin', 'read');
  // Artiklar is admin-only while in testing (articles:read).
  const canReadArticles = hasPermission('articles', 'read');
  const { isFreeTier } = usePlanStatus();

  const [theme, setTheme] = useState<'light' | 'dark'>(() =>
    (document.documentElement.getAttribute('data-bs-theme') as 'light' | 'dark') || 'light'
  );
  const [menuOpen, setMenuOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    document.documentElement.setAttribute('data-bs-theme', theme);
    try { localStorage.setItem('theme', theme); } catch {}
  }, [theme]);

  useEffect(() => {
    const close = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    if (menuOpen) document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [menuOpen]);

  useEffect(() => {
    document.body.style.overflow = drawerOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [drawerOpen]);

  const go = (path: string) => {
    navigate(path);
    setDrawerOpen(false);
    setMenuOpen(false);
  };

  const goUpgrade = () => {
    track('cta_click', 'navbar_upgrade');
    go('/account?upgrade=1');
  };

  const logout = () => {
    signOut();
    navigate('/login');
    setDrawerOpen(false);
    setMenuOpen(false);
  };

  return (
    <header className="nav2">
      <div className="nav2-inner">
        <button type="button" className="nav2-brand" onClick={() => go('/')} aria-label="Spotprisprognos">
          <Logo size="small" />
        </button>

        {/* Primary nav: segmented pill (desktop; mobile uses the drawer) */}
        <nav className="nav2-links">
          <button
            type="button"
            className={`nav2-link ${location.pathname === '/' ? 'active' : ''}`}
            onClick={() => go('/')}
            title={t('navbar.overview')}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 3v18h18" /><path d="M7 14l4-4 3 3 6-7" /></svg>
            <span className="nav2-link__label">{t('navbar.overview')}</span>
          </button>
          <button
            type="button"
            className={`nav2-link ${location.pathname === '/map' ? 'active' : ''}`}
            onClick={() => go('/map')}
            title={t('navbar.map')}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 4 3 6v14l6-2 6 2 6-2V4l-6 2-6-2Z" /><path d="M9 4v14M15 6v14" /></svg>
            <span className="nav2-link__label">{t('navbar.map')}</span>
          </button>
          <button
            type="button"
            className={`nav2-link ${location.pathname.startsWith('/forum') ? 'active' : ''}`}
            onClick={() => go('/forum')}
            title={t('navbar.forum')}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></svg>
            <span className="nav2-link__label">{t('navbar.forum')}</span>
          </button>
          {canReadArticles && (
            <button
              type="button"
              className={`nav2-link ${location.pathname.startsWith('/artiklar') ? 'active' : ''}`}
              onClick={() => go('/artiklar')}
              title={t('navbar.articles')}
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h13a3 3 0 0 1 3 3v13H7a3 3 0 0 1-3-3V4Z" /><path d="M8 8h8M8 12h8M8 16h5" /></svg>
              <span className="nav2-link__label">{t('navbar.articles')}</span>
            </button>
          )}
        </nav>

        {/* Contextual control (home: zone selector), centred in the bar. */}
        <div className="nav2-area">{centerSlot}</div>

        <div className="nav2-right">
          {/* Upgrade CTA — free-tier users only */}
          {isFreeTier && (
            <button type="button" className="nav2-cta" onClick={goUpgrade}>
              {t('navbar.upgrade')}
            </button>
          )}

          {/* Theme toggle — icon button */}
          <button
            type="button"
            className="nav2-icon"
            onClick={() => setTheme((t) => (t === 'light' ? 'dark' : 'light'))}
            aria-label={t('navbar.darkMode')}
            title={t('navbar.darkMode')}
          >
            {theme === 'light' ? (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79Z"/></svg>
            ) : (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="4"/><path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41"/></svg>
            )}
          </button>

          {/* User menu */}
          <div className="nav2-user-wrap" ref={menuRef}>
            <button
              type="button"
              className="nav2-user"
              onClick={() => setMenuOpen((o) => !o)}
              aria-haspopup="menu"
              aria-expanded={menuOpen}
            >
              <span className="nav2-avatar">{userInitial}</span>
              <span className="nav2-username">{userName}</span>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.55 }}><polyline points="6 9 12 15 18 9"/></svg>
            </button>

            {menuOpen && (
              <div className="nav2-menu" role="menu">
                <div className="nav2-menu-head">
                  <div className="nav2-avatar lg">{userInitial}</div>
                  <div>
                    <div className="nm-name">{userName}</div>
                    {authData?.email && <div className="nm-mail">{authData.email}</div>}
                  </div>
                </div>

                <button className="nav2-menu-item" onClick={() => go('/account')}>
                  {t('navbar.account')}
                </button>
                {isAdmin && (
                  <button className="nav2-menu-item" onClick={() => go('/admin')}>
                    {t('navbar.admin')}
                  </button>
                )}

                <div className="nav2-menu-sep" />
                <div className="nav2-menu-row">
                  <span className="nm-label">{t('navbar.language')}</span>
                  <div className="nav2-flag-row">
                    <button
                      type="button"
                      className={`nav2-flag ${language === 'sv' ? 'active' : ''}`}
                      onClick={() => setLanguage('sv')}
                      aria-label="Svenska"
                    ><FlagSE /></button>
                    <button
                      type="button"
                      className={`nav2-flag ${language === 'en' ? 'active' : ''}`}
                      onClick={() => setLanguage('en')}
                      aria-label="English"
                    ><FlagGB /></button>
                  </div>
                </div>

                <div className="nav2-menu-sep" />
                <button className="nav2-menu-item danger" onClick={logout}>
                  {t('navbar.logout')}
                </button>
              </div>
            )}
          </div>

          {/* Mobile burger */}
          <button
            type="button"
            className="nav2-burger"
            aria-label="Meny"
            aria-expanded={drawerOpen}
            onClick={() => setDrawerOpen((o) => !o)}
          >
            <span className={`burger-line ${drawerOpen ? 'a' : ''}`} />
            <span className={`burger-line ${drawerOpen ? 'b' : ''}`} />
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      <div className={`nav2-drawer ${drawerOpen ? 'open' : ''}`} role="dialog" aria-hidden={!drawerOpen}>
        <div className="nav2-drawer-inner">
          {isFreeTier && (
            <button className="nav2-drawer-link nav2-drawer-cta" onClick={goUpgrade}>{t('navbar.upgrade')}</button>
          )}
          <button className="nav2-drawer-link" onClick={() => go('/')}>{t('navbar.overview')}</button>
          <button className="nav2-drawer-link" onClick={() => go('/map')}>{t('navbar.map')}</button>
          <button className="nav2-drawer-link" onClick={() => go('/forum')}>{t('navbar.forum')}</button>
          {canReadArticles && (
            <button className="nav2-drawer-link" onClick={() => go('/artiklar')}>{t('navbar.articles')}</button>
          )}
          <button className="nav2-drawer-link" onClick={() => go('/account')}>{t('navbar.account')}</button>
          {isAdmin && (
            <button className="nav2-drawer-link" onClick={() => go('/admin')}>{t('navbar.admin')}</button>
          )}
          <div className="nav2-drawer-sep" />
          <div className="nav2-drawer-row">
            <span>{t('navbar.language')}</span>
            <div className="nav2-flag-row">
              <button className={`nav2-flag ${language === 'sv' ? 'active' : ''}`} onClick={() => setLanguage('sv')}><FlagSE /></button>
              <button className={`nav2-flag ${language === 'en' ? 'active' : ''}`} onClick={() => setLanguage('en')}><FlagGB /></button>
            </div>
          </div>
          <div className="nav2-drawer-row">
            <span>{t('navbar.darkMode')}</span>
            <button
              type="button"
              className={`nav2-toggle ${theme === 'dark' ? 'on' : ''}`}
              onClick={() => setTheme((t) => (t === 'light' ? 'dark' : 'light'))}
              aria-pressed={theme === 'dark'}
            ><span /></button>
          </div>
          <div className="nav2-drawer-sep" />
          <button className="nav2-drawer-link danger" onClick={logout}>{t('navbar.logout')}</button>
        </div>
      </div>
      {drawerOpen && <div className="nav2-scrim" onClick={() => setDrawerOpen(false)} />}
    </header>
  );
};
